import type { ReactNode } from 'react';

/**
 * Cartao de KPI do painel (RNF15).
 *
 * Duas regras de leitura de dado que este componente impoe por construcao:
 *
 *  - O valor NUNCA veste cor de serie. Numero colorido em superficie clara cai
 *    abaixo do contraste de texto e, pior, sugere uma categoria que nao existe.
 *    Identidade e estado vem do rotulo escrito e do ponto ao lado dele.
 *  - O valor chega PRONTO, ja formatado por lib/format.ts. Passar centavos para
 *    ca abriria a porta para alguem fazer conta dentro de um componente de
 *    apresentacao, que e onde float monetario costuma se infiltrar.
 *
 * O numero grande usa algarismos proporcionais (o padrao da fonte), nao
 * tabulares: `tabular-nums` da a todo digito a largura do zero e um "121" em
 * corpo grande fica visivelmente frouxo. Tabular fica reservado para coluna de
 * tabela, onde o alinhamento vertical importa.
 *
 * Nao existe seta de variacao ("+12% vs mes passado") de proposito: a API
 * entrega uma janela so. Quando houver comparacao real, o delta entra como
 * prop com glifo mais texto -- cor sozinha nunca comunica direcao.
 */

interface KpiCardProps {
  /** Frase curta, sem dois-pontos no fim. */
  readonly label: string;
  /** Valor ja formatado para exibicao. */
  readonly value: string;
  /** Linha de apoio: recorte, base de calculo, denominador. */
  readonly support?: string;
  /** Tendencia (sparkline) ou qualquer contexto visual, a direita do valor. */
  readonly aside?: ReactNode;
  /** Glifo decorativo do cartao. Nunca carrega informacao sozinho. */
  readonly icon?: ReactNode;
  /**
   * Figura principal da tela. Exatamente UMA por pagina: dois numeros gigantes
   * disputando atencao nao formam hierarquia nenhuma.
   */
  readonly emphasis?: boolean;
}

export function KpiCard({ label, value, support, aside, icon, emphasis = false }: KpiCardProps): JSX.Element {
  if (emphasis) {
    return (
      <article className="relative overflow-hidden rounded-2xl border border-line bg-hero px-6 py-6 text-white shadow-float sm:px-8 sm:py-8">
        <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-series-3/20 blur-3xl" />
        <div className="relative flex flex-wrap items-end justify-between gap-6">
          <div className="min-w-0">
            <h3 className="flex items-center gap-2 text-sm font-medium text-white/80">
              {icon}
              {label}
            </h3>
            <p className="mt-2 text-4xl font-semibold tracking-tight sm:text-6xl">{value}</p>
            {support === undefined ? null : <p className="mt-2 text-sm text-white/75">{support}</p>}
          </div>
          {aside === undefined ? null : <div className="shrink-0 text-white">{aside}</div>}
        </div>
      </article>
    );
  }

  return (
    <article className="flex flex-col gap-2 rounded-2xl border border-line bg-surface px-5 py-4 shadow-soft transition-shadow hover:shadow-float">
      <h3 className="flex items-center gap-2 text-sm font-medium text-ink-muted">
        {icon === undefined ? null : <span aria-hidden="true" className="grid h-7 w-7 place-items-center rounded-lg bg-brand/10 text-brand">{icon}</span>}
        {label}
      </h3>
      <div className="flex items-end justify-between gap-3">
        <p className="text-2xl font-semibold tracking-tight text-ink sm:text-3xl">{value}</p>
        {aside}
      </div>
      {support === undefined ? null : <p className="text-xs text-ink-subtle">{support}</p>}
    </article>
  );
}

/**
 * Esqueleto de carregamento com a mesma altura do cartao final.
 *
 * Reservar a altura evita o salto de layout quando o dado chega -- o painel
 * atualiza sozinho e um pulo de 80px a cada atualizacao e desorientador.
 */
export function KpiCardSkeleton({ emphasis = false }: { readonly emphasis?: boolean }): JSX.Element {
  return (
    <div
      aria-hidden="true"
      className={`flex animate-pulse flex-col gap-3 rounded-2xl border border-line bg-surface px-5 py-4 ${emphasis ? 'min-h-[10rem] sm:min-h-[12rem]' : ''}`}
    >
      <div className="h-4 w-28 rounded bg-ink/10" />
      <div className={`${emphasis ? 'h-14 w-64' : 'h-8 w-36'} rounded bg-ink/10`} />
      <div className="h-3 w-24 rounded bg-ink/10" />
    </div>
  );
}

/** Numero pequeno com rotulo, para a fileira secundaria de indicadores. */
export function StatChip({ label, value, tone = 'neutral' }: { readonly label: string; readonly value: string; readonly tone?: 'neutral' | 'voice' | 'whatsapp' }): JSX.Element {
  const ponto = tone === 'voice' ? 'bg-series-1' : tone === 'whatsapp' ? 'bg-series-3' : 'bg-ink-subtle';
  return (
    <div className="flex items-center gap-3 rounded-xl border border-line bg-surface px-4 py-3 shadow-soft">
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${ponto}`} />
      <div className="min-w-0">
        <p className="text-lg font-semibold tracking-tight text-ink">{value}</p>
        <p className="truncate text-xs text-ink-subtle">{label}</p>
      </div>
    </div>
  );
}
